/**
 * 名寄せ詳細アコーディオンコンポーネント
 *
 * 名寄せ処理の詳細（入力値、照合候補、特定結果）を表示
 */

import { Stack, Text, Group, Badge, Code, Accordion, Box } from "@mantine/core";
import { PlaygroundResult } from "../../services/playgroundService";

interface TimelineNormalizeDetailsProps {
  details: NonNullable<PlaygroundResult["normalizeDetails"]>;
}

export function TimelineNormalizeDetails({
  details,
}: TimelineNormalizeDetailsProps) {
  return (
    <Accordion
      variant="contained"
      radius="sm"
      mt="xs"
      multiple
      defaultValue={["step1", "step2", "step3"]}
    >
      {/* ステップ1: 入力値 */}
      <Accordion.Item value="step1">
        <Accordion.Control>
          <Text size="xs" fw={600}>
            ステップ1: 入力値
          </Text>
        </Accordion.Control>
        <Accordion.Panel>
          <Text size="sm" fw={500}>
            {details.inputName}
          </Text>
        </Accordion.Panel>
      </Accordion.Item>

      {/* ステップ2: 照合候補 */}
      <Accordion.Item value="step2">
        <Accordion.Control>
          <Text size="xs" fw={600}>
            ステップ2: 照合候補（{details.allPartners.length}件）
          </Text>
        </Accordion.Control>
        <Accordion.Panel>
          <Stack gap="sm">
            {details.allPartners.map((partner) => {
              const isMatched = details.candidates.some(
                (c) => c.partnerId === partner.id
              );
              return (
                <Box
                  key={partner.id}
                  p="sm"
                  style={{
                    backgroundColor: isMatched
                      ? "var(--mantine-color-green-light)"
                      : "transparent",
                    borderRadius: "4px",
                    border: isMatched
                      ? "1px solid var(--mantine-color-green-4)"
                      : "1px solid var(--mantine-color-gray-3)",
                  }}
                >
                  <Group gap="xs" mb={4}>
                    <Text size="xs" c="dimmed">
                      取引先名:
                    </Text>
                    <Text size="sm" fw={isMatched ? 600 : 500}>
                      {partner.name}
                    </Text>
                    {isMatched && (
                      <Badge size="xs" color="green">
                        一致
                      </Badge>
                    )}
                  </Group>
                  <Group gap="xs" align="flex-start">
                    <Text size="xs" c="dimmed">
                      名寄せパターン:
                    </Text>
                    {partner.patterns.length > 0 ? (
                      <Stack gap={2}>
                        {partner.patterns.map((pattern, idx) => (
                          <Code key={idx} style={{ fontSize: "11px" }}>
                            {pattern}
                          </Code>
                        ))}
                      </Stack>
                    ) : (
                      <Text size="xs" c="dimmed" fs="italic">
                        なし
                      </Text>
                    )}
                  </Group>
                </Box>
              );
            })}
          </Stack>
        </Accordion.Panel>
      </Accordion.Item>

      {/* ステップ3: 特定結果 */}
      <Accordion.Item value="step3">
        <Accordion.Control>
          <Text size="xs" fw={600}>
            ステップ3: 特定結果
          </Text>
        </Accordion.Control>
        <Accordion.Panel>
          {details.candidates.length > 0 ? (
            <Stack gap="sm">
              <Group gap="xs">
                <Text size="md" fw={600}>
                  {details.candidates[0]?.officialName}
                </Text>
                <Badge size="sm" color="green">
                  特定完了
                </Badge>
              </Group>

              <Text size="sm" fw={500}>
                マッチ方式（優先順）
              </Text>
              <Box
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "8px",
                }}
              >
                {[
                  {
                    key: "exact_pattern",
                    label: "パターン完全一致",
                    desc: "登録パターンと入力が完全に一致",
                  },
                  {
                    key: "fuzzy_official_name",
                    label: "正規名称一致",
                    desc: "正規名称を正規化して比較",
                  },
                  {
                    key: "fuzzy_pattern",
                    label: "パターン一致",
                    desc: "登録パターンを正規化して比較",
                  },
                  { key: "ai", label: "AI推論", desc: "AIが類似性から推論" },
                ].map((method) => {
                  const isUsed = details.matchType === method.key;
                  return (
                    <Box
                      key={method.key}
                      p="xs"
                      style={{
                        borderRadius: "4px",
                        border: isUsed
                          ? "1px solid var(--mantine-color-green-4)"
                          : "1px solid var(--mantine-color-gray-3)",
                        backgroundColor: isUsed
                          ? "var(--mantine-color-green-light)"
                          : "transparent",
                      }}
                    >
                      <Text
                        size="sm"
                        fw={isUsed ? 600 : 400}
                        c={isUsed ? "green.7" : undefined}
                      >
                        {isUsed && "✓ "}
                        {method.label}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {method.desc}
                      </Text>
                    </Box>
                  );
                })}
              </Box>

              {details.matchedPattern && (
                <Text size="sm" c="dimmed">
                  一致パターン: <Code>{details.matchedPattern}</Code>
                </Text>
              )}
            </Stack>
          ) : (
            <Text size="sm" c="dimmed">
              該当なし
            </Text>
          )}
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}
